import React, { useEffect, useState, createContext, useContext, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';
import '@/styles/manuscriptTheme.css';
import '@/styles/astroLab.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const login = () => {
    // Auth callback comes back as a hash fragment, so we redirect to
    // /birth-chart by default but ProtectedRoute saves the original
    // location in sessionStorage and AuthCallback honors it.
    const redirectUrl = window.location.origin + '/birth-chart';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout, login }}>
      {children}
    </AuthContext.Provider>
  );
}

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const location = useLocation();
  const hasProcessed = React.useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    
    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];
      
      fetch(`${API}/auth/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      })
        .then(res => res.json())
        .then(userData => {
          setUser(userData);
          window.history.replaceState({}, '', window.location.pathname);
          // If user was redirected here from a ProtectedRoute, send them back
          let target = '/profile';
          try {
            const saved = sessionStorage.getItem('post_login_redirect');
            if (saved) {
              sessionStorage.removeItem('post_login_redirect');
              target = saved;
            }
          } catch (e) {}
          navigate(target, { replace: true, state: { user: userData } });
        })
        .catch(error => {
          console.error('Auth callback failed:', error);
          navigate('/birth-chart', { replace: true });
        });
    } else {
      navigate('/birth-chart', { replace: true });
    }
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-primary-700">Completing authentication...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading, login } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    // Don't silently redirect — show a clear sign-in prompt that
    // returns the user to where they were after auth.
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6"
             data-testid="auth-required-screen">
        <div className="max-w-md w-full bg-white border border-stone-200 rounded-2xl p-8 shadow-sm text-center">
          <div className="text-[11px] uppercase tracking-[0.18em] text-violet-600 font-bold mb-2">
            Session Expired
          </div>
          <h2 className="text-2xl font-semibold text-stone-900 mb-2"
                style={{ fontFamily: 'Playfair Display, serif' }}>
            Please sign in to continue
          </h2>
          <p className="text-sm text-stone-600 mb-6">
            Your session has expired. Sign back in with Google to access
            <span className="font-semibold"> {location.pathname}</span>.
          </p>
          <button onClick={() => {
                  // Save where the user was trying to go
                  try { sessionStorage.setItem('post_login_redirect', location.pathname + location.search); } catch (e) {}
                  login();
                }}
                  data-testid="signin-required-btn"
                  className="inline-flex items-center justify-center gap-2 w-full bg-stone-900 hover:bg-stone-800 text-white font-medium py-3 px-5 rounded-lg transition">
            Sign in with Google
          </button>
          <button onClick={() => window.location.href = '/'}
                  data-testid="signin-home-btn"
                  className="mt-3 text-xs text-stone-500 hover:text-stone-700 underline">
            Go back home
          </button>
        </div>
      </div>
    );
  }

  return children;
}

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-white"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600" /></div>}>
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/panchanga" element={<PanchangaPage />} />
      <Route path="/muhurthas" element={<MuhurthasPage />} />
      <Route path="/birth-time-rectification" element={<Navigate to="/admin/btr-engine" replace />} />
      <Route path="/admin/btr-engine" element={<BtrEnginePage />} />
      <Route path="/muhurthas/details/:eventSlug" element={<MuhurthaDetailsPage />} />
      <Route path="/muhurthas/payment/:requestId" element={<MuhurthaPaymentPage />} />
      <Route path="/muhurthas/results/:requestId" element={<MuhurthaResultsPage />} />
      <Route path="/how-is-my-day" element={<HowIsMyDayPage />} />
      <Route path="/astro-rhythm" element={<AstroRhythmPage />} />
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/signup" element={<Navigate to="/" replace />} />
      <Route path="/consultations" element={<ConsultationsPage />} />
      <Route path="/courses" element={<CoursesPage />} />
      <Route path="/course/:courseId" element={<CourseDetailPage />} />
      <Route path="/blog" element={<Navigate to="/research" replace />} />
      <Route path="/blog/:slug" element={<BlogSlugRedirect />} />
      <Route path="/research" element={<ResearchHub />} />
      <Route path="/research/admin" element={<ProtectedRoute><ResearchAdmin /></ProtectedRoute>} />
      <Route path="/research-lab" element={<ProtectedRoute><ResearchPage /></ProtectedRoute>} />
      <Route path="/research-lab/phase01" element={<ProtectedRoute><ResearchPage /></ProtectedRoute>} />
      <Route path="/testimonials" element={<TestimonialsPage />} />
      <Route path="/forum" element={<ForumPage />} />
      <Route path="/birth-chart" element={<BirthChartPage />} />
      <Route path="/marriage-timing" element={<MarriageTimingPage />} />
      <Route path="/transit" element={<TransitDashboard />} />
      <Route path="/research/:slug" element={<ResearchArticle />} />
      <Route path="/cohort-validation" element={<CohortValidationDashboard />} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/astro-tools" element={<AstroToolsPage />} />
      <Route path="/astro-tools/mobile-compatibility" element={<MobileCompatibilityPage />} />
      <Route path="/astro-tools/vehicle-compatibility" element={<VehicleCompatibilityPage />} />
      <Route path="/astro-tools/vehicle-calibration" element={<VehicleCalibrationPage />} />
      <Route path="/astro-tools/divorce-potential" element={<DivorcePotentialPage />} />
      <Route path="/live-tools" element={<LiveToolsHub />} />
      <Route path="/live-tools/compatibility" element={<CompatibilityTool />} />
      <Route path="/live-tools/readiness" element={<ReadinessTool />} />
      <Route path="/live-tools/personality" element={<PersonalityTool />} />
      <Route path="/live-tools/couple-health" element={<CoupleHealthTool />} />
      <Route path="/live-tools/life-phases" element={<LifePhasesTool />} />
      <Route path="/live-tools/best-dates" element={<BestDatesTool />} />
      <Route path="/past-life-karmas" element={<PastLifeKarmasHub />} />
      <Route path="/past-life-karmas/sarpa-dosha" element={<SarpaDoshaPage />} />
      <Route path="/past-life-karmas/matru-shaapa" element={<MatrishapaPage />} />
      <Route path="/past-life-karmas/pitru-shaapa" element={<PitrishapaPage />} />
      <Route path="/past-life-karmas/bhratru-shaapa" element={<BhratrishapaPage />} />
      <Route path="/past-life-karmas/matula-shaapa" element={<MatulaShapaPage />} />
      <Route path="/past-life-karmas/stri-shaapa" element={<StriShapaPage />} />
      <Route path="/past-life-karmas/brahma-shaapa" element={<BrahmaShapaPage />} />
      <Route path="/astro-tools/birth-doshas" element={<BirthDoshasPage />} />
      <Route path="/astro-tools/upagraha" element={<UpagrahaPage />} />
      <Route path="/astro-tools/prana-pada" element={<Navigate to="/astro-tools" replace />} />
      <Route path="/admin/prana-pada" element={<PranaPadaToolPage />} />
      <Route path="/astro-tools/jaimini-sampath" element={<Navigate to="/astro-tools" replace />} />
      <Route path="/admin/jaimini-sampath" element={<JaiminiSampathPage />} />
      <Route path="/astro-tools/marriage-muhurtha" element={<MarriageMuhurthaPage />} />
      <Route path="/admin/sampath-calibration" element={<SampathCalibrationPage />} />
      <Route path="/admin/pranapada-calibration" element={<PranaPadaCalibrationPage />} />
      <Route path="/admin/pranapada-matrix" element={<PranaPadaMatrixPage />} />
      <Route path="/astro-tools/yoga-finder" element={<Navigate to="/astro-tools" replace />} />
      <Route path="/admin/yoga-strength" element={<YogaStrengthTool />} />
      <Route path="/astro-tools/turning-points" element={<Navigate to="/astro-tools" replace />} />
      <Route path="/admin/period-diagnostic" element={<BadTimePage />} />
      {/* Mokṣa engine LOCKED for calibration — restore by reverting to element={<Component/>} */}
      <Route path="/astro-tools/renunciation-lean" element={<Navigate to="/astro-tools" replace />} />
      <Route path="/astro-tools/moksha-compass" element={<Navigate to="/astro-tools" replace />} />
      <Route path="/astro-tools/moksha-report-preview" element={<Navigate to="/astro-tools" replace />} />
      <Route path="/astro-tools/moksha-full-report" element={<Navigate to="/astro-tools" replace />} />
      {/* Marriage Reality Lab locked — corrections in progress; redirect direct access */}
      <Route path="/astro-tools/marriage-reality" element={<Navigate to="/astro-tools" replace />} />
      <Route path="/astro-tools/gochara-effect" element={<GocharaEffectPage />} />
      <Route path="/astro-tools/divorce-calibration" element={<DivorceCalibrationPage />} />
      <Route path="/astro-tools/planets-education" element={<Navigate to="/astro-tools" replace />} />
      <Route path="/admin/education-calibration" element={<Navigate to="/astro-tools" replace />} />
      <Route path="/shop" element={<ShopPage />} />
      <Route path="/book/:consultationId" element={<ProtectedRoute><BookConsultationPage /></ProtectedRoute>} />
      <Route path="/cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
      <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/admin-dashboard" element={<AdminDashboard />} />
      <Route path="/astro-v2/admin" element={<AdminDashboardV2 />} />
      <Route path="/astro-v2/prediction" element={<PredictionPageV2 />} />
      <Route path="/astro-v2/compatibility" element={<CompatibilityPageV2 />} />
      <Route path="/astro-v2/timing" element={<TimingPageV2 />} />
      <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
      <Route path="/payment-cancel" element={<ProtectedRoute><PaymentCancelPage /></ProtectedRoute>} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster />
        </AuthProvider>
      </BrowserRouter>
    </div>
    </HelmetProvider>
  );
}

export default App;

// Import pages (these will be defined below)
const LandingPage = lazy(() => import('./pages/LandingPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const ConsultationsPage = lazy(() => import('./pages/ConsultationsPage'));
const CoursesPage = lazy(() => import('./pages/CoursesPage'));
const CourseDetailPage = lazy(() => import('./pages/CourseDetailPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
void BlogPage; void BlogPostPage;
const ResearchHub = lazy(() => import('./pages/research/ResearchHub'));
const ResearchArticle = lazy(() => import('./pages/research/ResearchArticle'));
const ResearchAdmin = lazy(() => import('./pages/research/ResearchAdmin'));

function BlogSlugRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/research/${slug}`} replace />;
}
const TestimonialsPage = lazy(() => import('./pages/TestimonialsPage'));
const ForumPage = lazy(() => import('./pages/ForumPage'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const AstroToolsPage = lazy(() => import('./pages/AstroToolsPage'));
const MarriageMuhurthaPage = lazy(() => import('./pages/MarriageMuhurthaPage'));
const MobileCompatibilityPage = lazy(() => import('./pages/MobileCompatibilityPage'));
const VehicleCompatibilityPage = lazy(() => import('./pages/VehicleCompatibilityPage'));
const VehicleCalibrationPage = lazy(() => import('./pages/VehicleCalibrationPage'));
const BookConsultationPage = lazy(() => import('./pages/BookConsultationPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const PaymentCancelPage = lazy(() => import('./pages/PaymentCancelPage'));
const BirthChartPage = lazy(() => import('./pages/BirthChartPage'));
const MarriageTimingPage = lazy(() => import('./pages/MarriageTimingPage'));
const TransitDashboard = lazy(() => import('./pages/TransitDashboard'));
const ResearchPage = lazy(() => import('./pages/ResearchPage'));
const CohortValidationDashboard = lazy(() => import('./pages/CohortValidationDashboard'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const PanchangaPage = lazy(() => import('./pages/PanchangaPage'));
const MuhurthasPage = lazy(() => import('./pages/MuhurthasPage'));
const BtrEnginePage = lazy(() => import('./pages/BtrEnginePage'));
const MuhurthaDetailsPage = lazy(() => import('./pages/MuhurthaDetailsPage'));
const MuhurthaPaymentPage = lazy(() => import('./pages/MuhurthaPaymentPage'));
const MuhurthaResultsPage = lazy(() => import('./pages/MuhurthaResultsPage'));
const HowIsMyDayPage = lazy(() => import('./pages/HowIsMyDayPage'));
const AstroRhythmPage = lazy(() => import('./pages/AstroRhythmPage'));
const PlanetsEducationPage = lazy(() => import('./pages/PlanetsEducationPage'));
const EducationCalibrationPage = lazy(() => import('./pages/EducationCalibrationPage'));
const DivorcePotentialPage = lazy(() => import('./pages/DivorcePotentialPage'));
const DivorceCalibrationPage = lazy(() => import('./pages/DivorceCalibrationPage'));
const AdminDashboardV2 = lazy(() => import('./pages/AdminDashboardV2'));
const PredictionPageV2 = lazy(() => import('./pages/PredictionPageV2'));
const CompatibilityPageV2 = lazy(() => import('./pages/CompatibilityPageV2'));
const TimingPageV2 = lazy(() => import('./pages/TimingPageV2'));
const LiveToolsHub = lazy(() => import('./pages/LiveToolsHub'));
const PastLifeKarmasHub = lazy(() => import('./pages/PastLifeKarmasHub'));
const SarpaDoshaPage = lazy(() => import('./pages/SarpaDoshaPage'));
const MatrishapaPage = lazy(() => import('./pages/MatrishapaPage'));
const PitrishapaPage = lazy(() => import('./pages/PitrishapaPage'));
const BhratrishapaPage = lazy(() => import('./pages/BhratrishapaPage'));
const MatulaShapaPage = lazy(() => import('./pages/MatulaShapaPage'));
const StriShapaPage = lazy(() => import('./pages/StriShapaPage'));
const BrahmaShapaPage = lazy(() => import('./pages/BrahmaShapaPage'));
const BirthDoshasPage = lazy(() => import('./pages/BirthDoshasPage'));
const YogaStrengthTool = lazy(() => import('./pages/YogaStrengthTool'));
const UpagrahaPage = lazy(() => import('./pages/UpagrahaPage'));
const PranaPadaToolPage = lazy(() => import('./pages/PranaPadaToolPage'));
const JaiminiSampathPage = lazy(() => import('./pages/JaiminiSampathPage'));
const SampathCalibrationPage = lazy(() => import('./pages/SampathCalibrationPage'));
const PranaPadaCalibrationPage = lazy(() => import('./pages/PranaPadaCalibrationPage'));
const PranaPadaMatrixPage = lazy(() => import('./pages/PranaPadaMatrixPage'));
const MarriageRealityPage = lazy(() => import('./pages/MarriageRealityPage'));
const GocharaEffectPage = lazy(() => import('./pages/GocharaEffectPage'));
const CompatibilityTool = lazy(() => import('./pages/tools/CompatibilityTool'));
const ReadinessTool = lazy(() => import('./pages/tools/ReadinessTool'));
const PersonalityTool = lazy(() => import('./pages/tools/PersonalityTool'));
const CoupleHealthTool = lazy(() => import('./pages/tools/CoupleHealthTool'));
const LifePhasesTool = lazy(() => import('./pages/tools/LifePhasesTool'));
const BestDatesTool = lazy(() => import('./pages/tools/BestDatesTool'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const BadTimePage = lazy(() => import('./pages/BadTimePage'));
const RenunciationLeanPage = lazy(() => import('./pages/RenunciationLeanPage'));
const MokshaReportPreviewPage = lazy(() => import('./pages/MokshaReportPreviewPage'));
const MokshaFullReportPage = lazy(() => import('./pages/MokshaFullReportPage'));
